import { ChevronLeft, ChevronRight } from "lucide-react";
import { cx, getPageItems } from "./utils";

const navButton =
  "inline-flex h-8 min-w-[32px] items-center justify-center gap-1 rounded-lg px-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-40";

export default function DataTablePagination({
  page,
  pageCount,
  pageSize,
  pageSizeOptions,
  totalItems,
  onPageChange,
  onPageSizeChange,
  disabled,
  pageSizeSelectId,
  labels,
}) {
  const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);
  const sizes = pageSizeOptions.includes(pageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, pageSize].sort((a, b) => a - b);

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-t border-app-primary px-4 py-3 text-sm text-app-muted">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <label htmlFor={pageSizeSelectId}>{labels.rowsPerPage}</label>
            <select
              id={pageSizeSelectId}
              value={pageSize}
              disabled={disabled}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-8 rounded-lg border border-app-primary bg-app-input py-0 pl-2.5 pr-8 text-sm text-app-primary focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              {sizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}
        <span aria-live="polite">{labels.range(start, end, totalItems)}</span>
      </div>

      <nav aria-label={labels.pagination} className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={disabled || page <= 1}
          aria-label={labels.previousPage}
          className={cx(navButton, "text-app-secondary hover:bg-app-hover hover:text-app-primary")}
        >
          <ChevronLeft size={16} strokeWidth={2} aria-hidden="true" />
          <span className="hidden sm:inline">{labels.previous}</span>
        </button>

        <span className="px-2 text-app-secondary sm:hidden">{labels.pageOf(page, pageCount)}</span>

        <ul className="hidden items-center gap-1 sm:flex">
          {getPageItems(page, pageCount).map((item, i) =>
            item === "ellipsis" ? (
              <li key={`e${i}`} aria-hidden="true" className="px-1 text-app-muted">
                …
              </li>
            ) : (
              <li key={item}>
                <button
                  type="button"
                  onClick={() => onPageChange(item)}
                  disabled={disabled}
                  aria-label={labels.page(item)}
                  aria-current={item === page ? "page" : undefined}
                  className={cx(
                    navButton,
                    item === page
                      ? "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:ring-blue-500/40"
                      : "text-app-secondary hover:bg-app-hover hover:text-app-primary",
                  )}
                >
                  {item}
                </button>
              </li>
            ),
          )}
        </ul>

        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={disabled || page >= pageCount}
          aria-label={labels.nextPage}
          className={cx(navButton, "text-app-secondary hover:bg-app-hover hover:text-app-primary")}
        >
          <span className="hidden sm:inline">{labels.next}</span>
          <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
        </button>
      </nav>
    </div>
  );
}
